import { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';

import { useLoader } from '../../contexts/LoaderContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

import { useTurmaService } from '../../services/turmaServices';
import { useTesteService } from '../../services/testeServices';
import { useParticipanteService } from '../../services/participanteServices';
import { useResultadoTesteService } from '../../services/resultadoTesteServices';
import { useMessageService } from '../../services/messageServices';
import { useEmpresaService } from '../../services/empresaServices';

import styles from './respondertestepage.module.css';
import logoPdf from '../../assets/logoPdf.jpg';

import { URLS } from '../../constants/urls';

Chart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const DEFAULT_COLOR = '#00AFA9';

// ✅ ATIVE AQUI
const DEBUG = false;

// 🔢 Limite fixo para lembretes do tipo "intervalo"
const MAX_RESPOSTAS_INTERVALO = 12;

const debug = {
  log: (...args) => { if (DEBUG) console.log('[ResponderTestePage]', ...args); },
  error: (...args) => { if (DEBUG) console.error('[ResponderTestePage]', ...args); },
  table: (...args) => { if (DEBUG && console.table) console.table(...args); },
  group: (...args) => { if (DEBUG && console.group) console.group('[ResponderTestePage]', ...args); },
  groupCollapsed: (...args) => { if (DEBUG && console.groupCollapsed) console.groupCollapsed('[ResponderTestePage]', ...args); },
  groupEnd: () => { if (DEBUG && console.groupEnd) console.groupEnd(); },
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const gerarGraficoBase64Generico = (scores = []) => new Promise((resolve) => {
  const labels = scores.map(s => String(s?.profile ?? '—'));
  const values = scores.map(s => Number(s?.percent ?? 0));
  const pointColors = scores.map(s => s?.color || DEFAULT_COLOR);

  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 800;
  canvas.style.display = 'none';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');

  const chart = new Chart(ctx, {
    type: 'radar',
    data: {
      labels,
      datasets: [{
        label: 'Distribuição por perfil',
        data: values,
        backgroundColor: 'rgba(0, 175, 169, 0.2)',
        borderColor: DEFAULT_COLOR,
        pointBackgroundColor: pointColors,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: DEFAULT_COLOR,
        borderWidth: 3
      }]
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        r: {
          beginAtZero: true, min: 0, max: 100,
          ticks: { stepSize: 20, color: '#444', backdropColor: 'transparent', font: { size: 14, weight: 'bold' } },
          pointLabels: { color: '#002D4C', font: { size: 16, weight: '600' } },
          grid: { color: '#ddd' },
          angleLines: { color: '#ccc' }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (c) => `${c.formattedValue}%` } }
      }
    }
  });

  const base64 = canvas.toDataURL('image/png');
  chart.destroy();
  document.body.removeChild(canvas);
  resolve(base64);
});

function drawWrappedText(ctx, text, x, y, maxWidth, {
  font = 'bold 20px Arial',
  color = '#222',
  lineHeight = 22,
  textAlign = 'left'
} = {}) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = textAlign;

  const words = String(text ?? '').split(/\s+/);
  let line = '';
  let height = 0;

  const flushLine = () => {
    if (line) {
      ctx.fillText(line, x, y + height);
      height += lineHeight;
      line = '';
    }
  };

  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    const w = ctx.measureText(test).width;

    if (w > maxWidth && line) {
      flushLine();
      if (ctx.measureText(words[i]).width > maxWidth) {
        let token = words[i];
        while (ctx.measureText(token).width > maxWidth && token.length > 1) {
          let cut = token.length - 1;
          while (cut > 1 && ctx.measureText(token.slice(0, cut)).width > maxWidth) cut--;
          const part = token.slice(0, cut);
          ctx.fillText(part, x, y + height);
          height += lineHeight;
          token = token.slice(cut);
        }
        line = token;
      } else {
        line = words[i];
      }
    } else {
      line = test;
    }
  }
  flushLine();

  ctx.restore();
  return height;
}

function desenharTabela(ctx, {
  x, y, width,
  titulo = null,
  linhas = [],
  headerBg = '#F1F1F1',
  zebraA = '#FFFFFF',
  zebraB = '#F9F9F9',
  headerH = 44,
  rowH = 40,
}) {
  ctx.save();

  if (titulo) {
    ctx.fillStyle = '#222';
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(titulo, x, y - 8);
  }

  ctx.fillStyle = headerBg;
  ctx.fillRect(x, y, width, headerH);

  ctx.fillStyle = '#222';
  ctx.font = 'bold 18px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('Perfil', x + 16, y + Math.floor(headerH * 0.68));
  ctx.textAlign = 'right';
  ctx.fillText('Percentual', x + width - 16, y + Math.floor(headerH * 0.68));

  ctx.strokeStyle = '#DDD';
  ctx.beginPath();
  ctx.moveTo(x, y + headerH + 0.5);
  ctx.lineTo(x + width, y + headerH + 0.5);
  ctx.stroke();

  let cursorY = y + headerH;
  linhas.forEach((linha, idx) => {
    const bg = idx % 2 === 0 ? zebraA : zebraB;
    ctx.fillStyle = bg;
    ctx.fillRect(x, cursorY, width, rowH);

    ctx.textAlign = 'left';
    ctx.fillStyle = linha.cor || DEFAULT_COLOR;
    ctx.font = '18px Arial';
    ctx.fillText(linha.label, x + 16, cursorY + Math.floor(rowH * 0.68));

    ctx.textAlign = 'right';
    ctx.fillStyle = '#222';
    ctx.fillText(linha.valor, x + width - 16, cursorY + Math.floor(rowH * 0.68));

    ctx.strokeStyle = '#EEE';
    ctx.beginPath();
    ctx.moveTo(x, cursorY + rowH + 0.5);
    ctx.lineTo(x + width, y + rowH + 0.5);
    ctx.stroke();

    cursorY += rowH;
  });

  const totalH = headerH + (linhas.length * rowH);
  ctx.strokeStyle = '#CCC';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, totalH);

  ctx.restore();
  return { height: totalH };
}

const gerarImagemEstiloPDFGenerico = async ({
  empresa,
  turma,
  testeNome,
  perfilLabel,
  scoresGeral = [],
  groups = [],
  chartBase64 = null,
}) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1000;
  canvas.height = 1400;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#F8F8F8'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#DDD'; ctx.lineWidth = 2;
  ctx.fillRect(80, 60, 840, 1280); ctx.strokeRect(80, 60, 840, 1280);

  const logo = new Image(); logo.src = logoPdf;
  await new Promise((resolve) => { logo.onload = () => { ctx.drawImage(logo, 100, 80, 180, 50); resolve(); }; });

  if (empresa?.logo) {
    const logoEmpresa = new Image(); logoEmpresa.crossOrigin = 'anonymous';
    logoEmpresa.src = `${URLS.IMG_PATH}${empresa.logo}`;
    await new Promise((resolve) => { logoEmpresa.onload = () => { ctx.drawImage(logoEmpresa, 720, 80, 160, 60); resolve(); }; });
  }

  ctx.fillStyle = '#222';
  ctx.font = 'bold 30px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(`${empresa?.nome || 'Empresa'} - ${turma?.nome || 'Turma'}`, canvas.width / 2, 170);

  if (testeNome) {
    ctx.fillStyle = '#666'; ctx.font = '600 22px Arial';
    ctx.fillText(`Teste: ${testeNome}`, canvas.width / 2, 205);
  }

  const innerX = 100;
  const innerW = 800;
  const topY = 240;
  let cursorY = topY;

  const toLinhas = (scoresArr = []) =>
    (scoresArr || []).map(s => ({
      label: String(s.profile ?? '—'),
      valor: `${Math.round(Number(s.percent ?? 0))}%`,
      cor: s?.color || DEFAULT_COLOR,
      pct: Number(s.percent ?? 0),
    }));

  if (Array.isArray(groups) && groups.length >= 2) {
    ctx.textAlign = 'left';
    ctx.fillStyle = '#222';
    ctx.font = 'bold 26px Arial';
    ctx.fillText('Perfis por Grupo', innerX, cursorY);
    cursorY += 18;

    cursorY += 70;

    const colGap = 40;
    const colW = Math.floor((innerW - colGap) / 2);
    const bottomLimit = 1280;

    for (let i = 0; i < groups.length; i += 2) {
      const leftG = groups[i];
      const rightG = groups[i + 1];
      let rowEndY = cursorY;

      if (leftG) {
        const xL = innerX;
        const labelL = leftG?.label || leftG?.id || `Grupo ${i + 1}`;
        const titleHL = drawWrappedText(ctx, `Grupo: ${String(labelL)}`, xL, cursorY, colW, {
          font: 'bold 20px Arial', color: '#222', lineHeight: 22, textAlign: 'left'
        });
        const linhasL = toLinhas(leftG?.scores);
        const { height: tableHL } = desenharTabela(ctx, {
          x: xL, y: cursorY + titleHL + 8, width: colW, titulo: null, linhas: linhasL
        });
        rowEndY = Math.max(rowEndY, cursorY + titleHL + 8 + tableHL);
      }

      if (rightG) {
        const xR = innerX + colW + colGap;
        const labelR = rightG?.label || rightG?.id || `Grupo ${i + 2}`;
        const titleHR = drawWrappedText(ctx, `Grupo: ${String(labelR)}`, xR, cursorY, colW, {
          font: 'bold 20px Arial', color: '#222', lineHeight: 22, textAlign: 'left'
        });
        const linhasR = toLinhas(rightG?.scores);
        const { height: tableHR } = desenharTabela(ctx, {
          x: xR, y: cursorY + titleHR + 8, width: colW, titulo: null, linhas: linhasR
        });
        rowEndY = Math.max(rowEndY, cursorY + titleHR + 8 + tableHR);
      }

      const nextY = rowEndY + 20;
      cursorY = nextY > bottomLimit ? bottomLimit : nextY;
    }

    ctx.fillStyle = '#AAA'; ctx.font = '20px Arial'; ctx.textAlign = 'center';
    ctx.fillText('Santé', canvas.width / 2, 1320);
    return canvas.toDataURL('image/png');
  }

  ctx.textAlign = 'left';
  ctx.fillStyle = '#222';
  ctx.font = 'bold 26px Arial';
  ctx.fillText('Resultado', innerX, cursorY);
  cursorY += 18;

  cursorY += 50;

  let blocoTitulo = 'Distribuição por perfil';
  let scoresParaTabela = scoresGeral;

  if (Array.isArray(groups) && groups.length === 1) {
    const g0 = groups[0];
    blocoTitulo = `Grupo: ${String(g0?.label || g0?.id || '—')}`;
    scoresParaTabela = g0?.scores || scoresGeral;
  }

  const linhas = (scoresParaTabela || []).map(s => ({
    label: String(s.profile ?? '—'),
    valor: `${Math.round(Number(s.percent ?? 0))}%`,
    cor: s?.color || DEFAULT_COLOR,
    pct: Number(s.percent ?? 0),
  }));

  const { height: tabelaHeight } = desenharTabela(ctx, {
    x: innerX,
    y: cursorY,
    width: 800,
    titulo: blocoTitulo,
    linhas
  });

  cursorY = cursorY + tabelaHeight + 24;

  if (chartBase64) {
    const img = new Image();
    img.src = chartBase64;

    await new Promise((resolve) => {
      img.onload = () => {
        const targetW = 800;
        const targetH = 420;

        const ratio = Math.min(targetW / img.width, targetH / img.height);
        const drawW = Math.floor(img.width * ratio);
        const drawH = Math.floor(img.height * ratio);

        const drawX = innerX + Math.floor((targetW - drawW) / 2);
        const drawY = cursorY;

        ctx.drawImage(img, drawX, drawY, drawW, drawH);
        resolve();
      };
    });
  }

  ctx.fillStyle = '#AAA';
  ctx.font = '20px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Santé', canvas.width / 2, 1320);

  return canvas.toDataURL('image/png');
};

function ResponderTestePage() {
  const { idTurma } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    obterTurmaPorId,
    associarParticipanteATurma,
    listarTodosLembretesTurma,
  } = useTurmaService();
  const { obterEmpresaPorId } = useEmpresaService();
  const { obterTestePorId } = useTesteService();
  const { listarParticipantesPorTurma } = useParticipanteService();
  const {
    criarResultado,
    verificarExistenciaResultado,
    readResult,
    gerarPdfDevolutiva,
    getLastResult
  } = useResultadoTesteService();
  const { enviarMensagem, enviarMensagemPdf } = useMessageService();

  const { showLoader, hideLoader } = useLoader();
  const { showToast } = useToast();
  const { usuario, logout } = useAuth();

  const redirectToLogin = (reason = '') => {
    debug.groupCollapsed('redirectToLogin');
    debug.log('reason:', reason);
    debug.log('idTurma param:', idTurma);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    debug.groupEnd();

    navigate(
      `/participante/login?turma=${encodeURIComponent(idTurma || '')}&dest=res`,
      { replace: true, state: { from: location } }
    );
  };

  const [respostas, setRespostas] = useState({});
  // ✅ Agora começa na "etapa inicial" (descrição do teste)
  const [paginaAtual, setPaginaAtual] = useState(-1);
  const [embaralhamentoAtual, setEmbaralhamentoAtual] = useState({});

  const EMBARALHAR_OPCOES = false;

  const [turma, setTurma] = useState(null);
  const [teste, setTeste] = useState(null);
  const [nomeTeste, setNomeTeste] = useState(null);
  const [idTeste, setIdTeste] = useState(null);
  const [empresa, setEmpresa] = useState(null);

  // ✅ NOVO: descrição vinda de testeResponse.data.descricao
  const [descricaoTeste, setDescricaoTeste] = useState('');

  const [mensagemRetorno, setMensagemRetorno] = useState('');
  const [devolutiva, setDevolutiva] = useState(0);

  const [mostrarModalFinalizacao, setMostrarModalFinalizacao] = useState(false);
  const [idResultadoCriado, setIdResultadoCriado] = useState(null);

  // ✅ Para aumentar slider em telas menores (CSS você ajusta depois)
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const apply = () => setIsSmallScreen(!!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  const totalPerguntas = teste?.questions?.length || 0;

  // ✅ Etapas: 1 etapa de introdução + perguntas
  const totalEtapas = (totalPerguntas || 0) + 1;
  const etapaAtual = paginaAtual + 1; // -1 => 0 (intro), 0 => 1 (pergunta 1), etc.
  const progresso = totalEtapas > 0 ? (etapaAtual / totalEtapas) * 100 : 0;

  const isIntroStep = paginaAtual === -1;

  useEffect(() => {
    debug.groupCollapsed('mount');
    debug.log('params.idTurma:', idTurma);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    try {
      debug.log('idTurma decodeURIComponent:', decodeURIComponent(idTurma || ''));
    } catch (e) {
      debug.error('decodeURIComponent falhou:', e);
    }
    debug.groupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (index, value) => {
    setRespostas(prev => ({ ...prev, [index]: value }));
  };

  const movePriorityItem = (fromIdx, toIdx) => {
    setRespostas(prev => {
      const atualPergunta = teste?.questions?.[paginaAtual];
      if (atualPergunta?.type !== 'priority') return prev;

      const currentOrder = Array.isArray(prev[paginaAtual])
        ? prev[paginaAtual]
        : Object.keys((atualPergunta.options) || {});

      if (toIdx < 0 || toIdx >= currentOrder.length) return prev;
      const nova = [...currentOrder];
      const [m] = nova.splice(fromIdx, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtual]: nova };
    });
  };

  const handleClickUp = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItem(idx, idx - 1);
  };

  const handleClickDown = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItem(idx, idx + 1);
  };

  const isPerguntaRespondida = (perguntaObj, respostaVal) => {
    if (!perguntaObj) return false;
    const tipo = perguntaObj.type;

    if (tipo === 'closed') return (respostaVal ?? '').toString().trim() !== '';
    if (tipo === 'priority') return Array.isArray(respostaVal) && respostaVal.length > 0;
    if (tipo === 'scale') return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
    if (tipo === 'open') {
      if (perguntaObj.hasDate) {
        if (!respostaVal || typeof respostaVal !== 'object') return false;
        const text = (respostaVal.text ?? '').toString().trim();
        const date = (respostaVal.date ?? '').toString().trim();
        return text !== '' && date !== '';
      }
      return (respostaVal ?? '').toString().trim() !== '';
    }
    return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
  };

  // ==========================
  // ✅ NOVO: Helpers Prefill (último resultado)
  // ==========================
  const safeJsonParse = (raw, fallback) => {
    try {
      const v = JSON.parse(raw);
      return v ?? fallback;
    } catch {
      return fallback;
    }
  };

  const buildOpenAnswerMapsFromLastResult = (resultadoRow) => {
    const arr = Array.isArray(resultadoRow?.resposta)
      ? resultadoRow.resposta
      : safeJsonParse(resultadoRow?.resposta ?? '[]', []);

    const openById = new Map();
    const openByQuestionText = new Map();

    for (const item of (Array.isArray(arr) ? arr : [])) {
      const tipo = (item?.tipo ?? item?.type) || '';
      if (tipo !== 'open') continue;

      const id = item?.id ? String(item.id) : null;
      const qText = item?.pergunta ? String(item.pergunta) : null;

      const text = (item?.textoResposta ?? item?.resposta ?? '').toString();
      const date = (item?.dataResposta ?? '').toString();

      const payload = { text, date };

      if (id) openById.set(id, payload);
      if (qText) openByQuestionText.set(qText, payload);
    }

    return { openById, openByQuestionText };
  };

  const applyPrefillOpenAnswers = (estrutura, lastResultRow) => {
    if (!estrutura?.questions?.length) return;

    const { openById, openByQuestionText } = buildOpenAnswerMapsFromLastResult(lastResultRow);

    setRespostas((prev) => {
      const next = { ...prev };

      estrutura.questions.forEach((q, idx) => {
        if (q?.type !== 'open') return;

        const qId = q?.id ?? q?.questionId ?? q?.qid ?? null;
        const qText = q?.question ?? null;

        const found =
          (qId ? openById.get(String(qId)) : null) ||
          (qText ? openByQuestionText.get(String(qText)) : null);

        if (!found) return;

        // Só preenche se ainda estiver vazio (pra não sobrescrever caso o usuário já tenha digitado algo)
        if (q?.hasDate) {
          const cur = next[idx];
          const curObj = (cur && typeof cur === 'object') ? cur : { text: '', date: '' };
          const curText = (curObj.text ?? '').toString().trim();
          const curDate = (curObj.date ?? '').toString().trim();

          if (curText === '' && curDate === '') {
            next[idx] = { text: found.text ?? '', date: found.date ?? '' };
          }
        } else {
          const cur = (next[idx] ?? '').toString();
          if (cur.trim() === '') {
            next[idx] = (found.text ?? '').toString();
          }
        }
      });

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!usuario?.id) {
      showToast('Faça login novamente para enviar suas respostas.', 3500, 'error');
      return redirectToLogin('handleSubmit: usuario.id ausente');
    }

    if (!teste?.questions || !idTeste) {
      showToast('Teste inválido. Recarregue a página.', 3500, 'error');
      return;
    }

    const respostasFormatadas = Object.entries(respostas).map(([indiceStr, valor]) => {
      const indice = Number(indiceStr);
      const perguntaObj = teste.questions[indice];

      if (!perguntaObj) {
        return { pergunta: '(desconhecida)', resposta: valor, textoResposta: valor };
      }

      const perguntaId = perguntaObj.id ?? perguntaObj.questionId ?? perguntaObj.qid ?? null;

      if (perguntaObj.type === 'priority') {
        const orderLetters = Array.isArray(valor) ? valor : Object.keys(perguntaObj.options || {});
        const orderTexts = orderLetters.map((L) => (perguntaObj.options || {})[L]);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'priority',
          resposta: orderLetters,
          textoResposta: orderTexts
        };
      }

      if (perguntaObj.type === 'closed') {
        const letra = valor;
        const textoResposta = (perguntaObj.options || {})[letra];
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'closed',
          resposta: letra,
          textoResposta
        };
      }

      if (perguntaObj.type === 'scale') {
        const numVal = Number(valor);
        const labels = (perguntaObj.scale && perguntaObj.scale.labels) || {};
        const textoResposta = labels[numVal] ?? String(numVal);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'scale',
          resposta: numVal,
          textoResposta
        };
      }

      if (perguntaObj.type === 'open') {
        let texto = '';
        let dataResposta = undefined;

        if (valor && typeof valor === 'object') {
          texto = (valor.text ?? '').toString();
          dataResposta = (valor.date ?? '') || undefined;
        } else {
          texto = (valor ?? '').toString();
        }

        const base = {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'open',
          resposta: texto,
          textoResposta: texto
        };

        if (perguntaObj.hasDate && dataResposta) {
          return { ...base, dataResposta };
        }

        return base;
      }

      return {
        id: perguntaId || undefined,
        pergunta: perguntaObj.question,
        tipo: perguntaObj.type || 'desconhecido',
        resposta: valor,
        textoResposta: valor
      };
    });

    try {
      showLoader('Enviando respostas');

      debug.groupCollapsed('criarResultado payload');
      debug.log({
        idParticipante: usuario.id,
        idTeste,
        idTurma,
        respostaLen: JSON.stringify(respostasFormatadas)?.length
      });
      debug.groupEnd();

      const response = await criarResultado({
        idParticipante: usuario.id,
        idTeste,
        idTurma,
        resposta: JSON.stringify(respostasFormatadas)
      });

      debug.log('criarResultado response:', response);

      if (response?.success) {
        setIdResultadoCriado(response.id);
        setMostrarModalFinalizacao(true);
      } else {
        if (response?.message?.toLowerCase?.().includes('token')) {
          logout();
          showToast('Sua sessão expirou. Faça login novamente.', 4000, 'error');
          return redirectToLogin('criarResultado: token');
        }
        showToast(response?.message || 'Erro ao enviar resultado', 4000, 'error');
      }
    } catch (err) {
      debug.error('criarResultado erro:', err);
      showToast('Erro inesperado ao enviar o resultado', 4000, 'error');
    } finally {
      hideLoader();
    }
  };

  // ============================================================
  // ✅ AJUSTADO: Enviar pelo WhatsApp (PDF ou Imagem)
  // - PDF agora segue o endpoint novo: precisa enviar pdfUrl
  // ============================================================
  const handleEnviarWhatsApp = async () => {
    if (!usuario?.id) {
      showToast('Faça login novamente para enviar pelo WhatsApp.', 3500, 'error');
      return redirectToLogin('handleEnviarWhatsApp: usuario.id ausente');
    }

    if (!idResultadoCriado) {
      showToast('Resultado ainda não foi criado.', 4000, 'error');
      return;
    }

    const tokenExpired = (obj) =>
      !!obj?.message?.toLowerCase?.().includes('token');

    try {
      // 1) Buscar resultado completo (serve para ambos fluxos)
      showLoader('Preparando envio');

      const res = await readResult(idResultadoCriado);

      if (tokenExpired(res)) {
        logout();
        showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
        return redirectToLogin('readResult: token');
      }

      if (!res?.success || !res?.data) {
        debug.error('readResult falhou:', res);
        showToast('Erro ao obter resultado.', 4000, 'error');
        return;
      }

      const testeNome = res?.data?.meta?.testeNome || nomeTeste || 'Resultado do Teste';
      const mensagem =
        (mensagemRetorno && mensagemRetorno.trim())
          ? mensagemRetorno.trim()
          : 'Obrigado por participar!';

      // 2) Se devolutiva=1 -> fluxo PDF (gera PDF, monta pdfUrl, envia)
      if (Number(devolutiva) === 1) {
        showLoader('Gerando PDF');

        const pdfRes = await gerarPdfDevolutiva(idResultadoCriado);

        if (tokenExpired(pdfRes)) {
          logout();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('gerarPdfDevolutiva: token');
        }

        if (!pdfRes?.success) {
          debug.error('gerarPdfDevolutiva falhou:', pdfRes);
          showToast(pdfRes?.message || 'Erro ao gerar PDF.', 4000, 'error');
          return;
        }

        const relative =
          pdfRes?.relative_path ||
          pdfRes?.data?.relative_path ||
          pdfRes?.pdfRelativePath ||
          pdfRes?.data?.pdfRelativePath ||
          pdfRes?.data?.relativePath ||
          pdfRes?.relativePath ||
          pdfRes?.data?.path ||
          pdfRes?.path;

        if (!relative) {
          debug.error('PDF gerado mas sem relative_path:', pdfRes);
          showToast('PDF gerado, mas o servidor não retornou o caminho do arquivo.', 4000, 'error');
          return;
        }

        const pdfUrl = `${URLS.IMG_PATH}reports/${relative}`;

        // ✅ endpoint aceita payload direto ou dentro de "data"
        const payloadPdf = {
          idParticipante: usuario.id,
          teste: testeNome,
          mensagem,
          empresaNome: empresa?.nome || '',
          pdfUrl,
          pdfRelativePath: relative,
        };

        showLoader('Enviando PDF pelo WhatsApp');

        const envioPdf = await enviarMensagemPdf(payloadPdf);

        if (tokenExpired(envioPdf)) {
          logout();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('enviarMensagemPdf: token');
        }

        if (envioPdf?.success) {
          showToast('Relatório enviado pelo WhatsApp!', 3000, 'success');
          navigate('/participante/page');
        } else {
          debug.error('Erro no envio do PDF pelo WhatsApp:', envioPdf);
          showToast(envioPdf?.message || 'Erro ao enviar o relatório pelo WhatsApp.', 4000, 'error');
        }
        return;
      }

      // 3) devolutiva=0 -> fluxo IMAGEM
      showLoader('Gerando imagem do resultado');

      const data = res.data;
      const overallScores = data?.overall?.scores || data?.scores || [];
      const grupos = Array.isArray(data?.groups) ? data.groups : [];

      // 2+ grupos: sem gráfico (apenas tabelas por grupo)
      // 1 grupo: gráfico do grupo
      // 0 grupo: gráfico geral
      let chartScores = overallScores;
      if (grupos.length === 1) chartScores = grupos[0]?.scores || overallScores;

      const deveGerarGrafico = grupos.length < 2;
      const graficoBase64 = deveGerarGrafico ? await gerarGraficoBase64Generico(chartScores) : null;

      const imagemResultado = await gerarImagemEstiloPDFGenerico({
        empresa,
        turma,
        testeNome,
        perfilLabel: (data?.overall?.predominant?.profiles || []).join(' + ') || '—',
        scoresGeral: overallScores,
        groups: grupos,
        chartBase64: graficoBase64
      });

      if (!imagemResultado || imagemResultado.length < 30) {
        showToast('Erro ao gerar a imagem.', 4000, 'error');
        return;
      }

      const payloadImg = {
        idParticipante: usuario.id,
        file: imagemResultado,
        teste: testeNome,
        mensagem
      };

      const envioImg = await enviarMensagem(payloadImg);

      if (tokenExpired(envioImg)) {
        logout();
        showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
        return redirectToLogin('enviarMensagem: token');
      }

      if (envioImg?.success) {
        showToast('Mensagem enviada pelo WhatsApp!', 3000, 'success');
        navigate('/participante/page');
      } else {
        debug.error('Erro no envio pelo WhatsApp:', envioImg);
        showToast(envioImg?.message || 'Erro ao enviar pelo WhatsApp.', 4000, 'error');
      }
    } catch (error) {
      debug.error('Erro inesperado no envio pelo WhatsApp:', error);
      showToast('Erro inesperado no envio.', 4000, 'error');
    } finally {
      hideLoader();
    }
  };
  // ============================================================

  const handleProximo = () => {
    // ✅ Intro -> começa o teste
    if (isIntroStep) {
      if (totalPerguntas > 0) setPaginaAtual(0);
      return;
    }

    const perguntaAtualObj = teste?.questions?.[paginaAtual];
    const respostaAtualVal = respostas[paginaAtual];

    if (paginaAtual < totalPerguntas - 1 && isPerguntaRespondida(perguntaAtualObj, respostaAtualVal)) {
      setPaginaAtual(paginaAtual + 1);
    }
  };

  const handleAnterior = () => {
    // ✅ Voltar da primeira pergunta volta para a intro
    if (paginaAtual === 0) return setPaginaAtual(-1);
    if (paginaAtual > 0) setPaginaAtual(paginaAtual - 1);
  };

  useEffect(() => {
    const carregarTurmaETeste = async () => {
      if (!idTurma) {
        debug.error('idTurma está vazio! params:', { idTurma });
        showToast('Link inválido: turma não informada.', 4000, 'error');
        return navigate('/participante/page');
      }

      if (!usuario?.id) {
        showToast('Faça login para responder o teste.', 3500, 'error');
        return redirectToLogin('carregarTurmaETeste: usuario.id ausente');
      }

      let idTurmaDecoded = idTurma;
      try { idTurmaDecoded = decodeURIComponent(idTurma); } catch {}

      debug.groupCollapsed('carregarTurmaETeste: início');
      debug.log('idTurma (raw):', idTurma);
      debug.log('idTurma (decoded):', idTurmaDecoded);
      debug.log('usuario.id:', usuario.id);
      debug.groupEnd();

      showLoader('Buscando dados da turma');

      debug.log('Chamando obterTurmaPorId com:', idTurma);
      const response = await obterTurmaPorId(idTurma);

      debug.groupCollapsed('obterTurmaPorId response');
      debug.log('raw response:', response);
      debug.table({
        success: response?.success,
        message: response?.message,
        id: response?.data?.id,
        nome: response?.data?.nome,
        idEmpresa: response?.data?.idEmpresa,
        idTeste: response?.data?.idTeste,
        status: response?.data?.status,
        data_limite: response?.data?.data_limite,
      });
      debug.groupEnd();

      if (!response?.success) {
        hideLoader();

        showToast(response?.message || 'Erro ao carregar dados da turma.', 4000, 'error');

        if (response?.message?.toLowerCase?.().includes('token')) {
          logout();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('obterTurmaPorId: token');
        }

        debug.error('Falha ao obter turma. idTurma usado:', idTurma);
        return;
      }

      setMensagemRetorno(response?.data?.mensagem_retorno ?? '');
      setDevolutiva(Number(response?.data?.devolutiva ?? 0));
      setTurma(response.data);

      try {
        const empresaResponse = await obterEmpresaPorId(response.data.idEmpresa);
        debug.log('obterEmpresaPorId response:', empresaResponse);
        if (empresaResponse?.success) setEmpresa(empresaResponse.data);
        if (empresaResponse?.message?.toLowerCase?.().includes('token')) {
          logout();
          hideLoader();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('obterEmpresaPorId: token');
        }
      } catch (err) {
        debug.error('Erro inesperado ao buscar empresa:', err);
      }

      if (Number(response?.data?.status ?? 0) === 0) {
        showToast('Essa turma ainda está inativa!', 5000, 'error');
        hideLoader();
        return navigate('/participante/page');
      }

      const hoje = new Date();
      const dataLimiteStr = response?.data?.data_limite;
      if (dataLimiteStr) {
        const dataLimite = new Date(dataLimiteStr + 'T23:59:59');
        if (hoje > dataLimite) {
          showToast('O prazo para responder esse teste expirou.', 5000, 'error');
          hideLoader();
          return navigate('/participante/page');
        }
      }

      const participantesResponse = await listarParticipantesPorTurma(idTurma);
      debug.log('listarParticipantesPorTurma response:', participantesResponse);

      if (participantesResponse?.success) {
        const vinculado = safeArray(participantesResponse.data).some(p => p?.id === usuario.id);

        debug.log('vinculado?', vinculado);

        if (!vinculado) {
          const associarResponse = await associarParticipanteATurma(usuario.id, idTurma);
          debug.log('associarParticipanteATurma response:', associarResponse);

          if (associarResponse?.success) {
            showToast('Vínculo com a turma realizado com sucesso!', 3000, 'success');
          } else {
            if (associarResponse?.message?.toLowerCase?.().includes('token')) {
              logout();
              hideLoader();
              showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
              return redirectToLogin('associarParticipanteATurma: token');
            }

            showToast('Erro ao associar você à turma.', 3000, 'error');
            hideLoader();
            return navigate('/participante/page');
          }
        }
      } else {
        if (participantesResponse?.message?.toLowerCase?.().includes('token')) {
          logout();
          hideLoader();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('listarParticipantesPorTurma: token');
        }

        showToast('Erro ao verificar vínculo do participante.', 3000, 'error');
        hideLoader();
        return navigate('/participante/page');
      }

      const idTesteDaTurma = response?.data?.idTeste;

      if (!idTesteDaTurma) {
        showToast('Turma sem teste associado.', 3000, 'error');
        hideLoader();
        return navigate('/participante/page');
      }
      setIdTeste(idTesteDaTurma);

      const existenciaRes = await verificarExistenciaResultado({
        idTurma,
        idTeste: idTesteDaTurma,
        idParticipante: usuario.id
      });

      debug.log('verificarExistenciaResultado response:', existenciaRes);

      if (existenciaRes?.message?.toLowerCase?.().includes('token')) {
        logout();
        hideLoader();
        showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
        return redirectToLogin('verificarExistenciaResultado: token');
      }

      if (existenciaRes?.existe) {
        const acompanhamentoBruto = response?.data?.acompanhamento;
        const acompanhamentoAtivo = Boolean(
          typeof acompanhamentoBruto === 'boolean'
            ? acompanhamentoBruto
            : Number(acompanhamentoBruto ?? 0)
        );
        const tipoLembrete = response?.data?.tipo_lembrete;

        debug.log('acompanhamentoBruto:', acompanhamentoBruto);
        debug.log('acompanhamentoAtivo:', acompanhamentoAtivo);
        debug.log('tipoLembrete:', tipoLembrete);
        debug.log('rows:', existenciaRes?.rows);

        if (!acompanhamentoAtivo) {
          showToast('Você já respondeu esse teste.', 3000, 'info');
          hideLoader();
          return navigate('/participante/page');
        }

        const totalRespostas = Number(existenciaRes?.rows ?? 0);

        if (tipoLembrete === 'intervalo') {
          if (totalRespostas >= MAX_RESPOSTAS_INTERVALO) {
            showToast(
              'Você já respondeu esse teste o número máximo de vezes permitido.',
              4000,
              'info'
            );
            hideLoader();
            return navigate('/participante/page');
          }
        } else {
          try {
            const lembretesRes = await listarTodosLembretesTurma(idTurma);
            debug.log('listarTodosLembretesTurma response:', lembretesRes);

            if (lembretesRes?.message?.toLowerCase?.().includes('token')) {
              logout();
              hideLoader();
              showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
              return redirectToLogin('listarTodosLembretesTurma: token');
            }

            if (lembretesRes?.success && Array.isArray(lembretesRes.data)) {
              const totalLembretes = lembretesRes.data.length;

              if (totalRespostas >= totalLembretes) {
                showToast(
                  'Você já respondeu esse teste o número máximo de vezes permitido.',
                  4000,
                  'info'
                );
                hideLoader();
                return navigate('/participante/page');
              }
            } else {
              showToast('Você já respondeu esse teste.', 3000, 'info');
              hideLoader();
              return navigate('/participante/page');
            }
          } catch (err) {
            debug.error('Erro ao listar lembretes da turma:', err);
            showToast('Você já respondeu esse teste.', 3000, 'info');
            hideLoader();
            return navigate('/participante/page');
          }
        }
      }

      const testeResponse = await obterTestePorId(idTesteDaTurma);
      debug.log('obterTestePorId response:', testeResponse);

      if (!testeResponse?.success) {
        hideLoader();

        if (testeResponse?.message?.toLowerCase?.().includes('token')) {
          logout();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('obterTestePorId: token');
        }

        showToast('Erro ao carregar teste da turma.', 3000, 'error');
        return navigate('/participante/page');
      }

      // ✅ NOVO: pega descrição do teste
      setDescricaoTeste((testeResponse?.data?.descricao ?? '').toString());

      const raw = testeResponse?.data?.estrutura;
      let estruturaParse = null;

      if (raw && typeof raw === 'string') {
        try { estruturaParse = JSON.parse(raw); } catch { estruturaParse = null; }
      } else if (raw && typeof raw === 'object') {
        estruturaParse = raw;
      }

      if (!estruturaParse || !Array.isArray(estruturaParse.questions)) {
        showToast('Estrutura do teste inválida.', 3000, 'error');
        hideLoader();
        return navigate('/participante/page');
      }

      setNomeTeste(testeResponse?.data?.nome || '');
      setTeste(estruturaParse);

      // ✅ NOVO: tenta preencher perguntas abertas com último resultado (silencioso se não existir)
      try {
        debug.groupCollapsed('getLastResult: payload');
        debug.log('usuario.id:', usuario?.id);
        debug.log('idTurma (param raw):', idTurma);
        let idTurmaDecodedLocal = idTurma;
        try { idTurmaDecodedLocal = decodeURIComponent(idTurma || ''); } catch {}
        debug.log('idTurma (decoded):', idTurmaDecodedLocal);
        debug.groupEnd();

        const payload = {
          idParticipante: usuario?.id,
          idTurma: idTurma,
        };

        debug.log('getLastResult: enviando payload:', payload);

        const lastRes = await getLastResult(payload);

        debug.groupCollapsed('getLastResult: response');
        debug.log(lastRes);
        debug.groupEnd();

        if (lastRes?.message?.toLowerCase?.().includes('token')) {
          logout();
          hideLoader();
          showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
          return redirectToLogin('getLastResult: token');
        }

        const lastRow = lastRes?.data ?? null;
        if (lastRes?.success && lastRow) {
          applyPrefillOpenAnswers(estruturaParse, lastRow);
        } else {
          debug.log('getLastResult: sem dados para prefill (success=false ou data=null).');
        }
      } catch (err) {
        debug.error('getLastResult erro (silencioso):', err);
      }

      // ✅ garante que a intro seja sempre a primeira etapa após carregar
      setPaginaAtual(-1);

      hideLoader();
    };

    carregarTurmaETeste();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTurma, usuario?.id]);

  // ✅ Só prepara estado das perguntas quando não está na intro
  useEffect(() => {
    if (paginaAtual < 0) {
      setEmbaralhamentoAtual({});
      return;
    }

    const perguntaAtual = teste?.questions?.[paginaAtual];
    if (!perguntaAtual) {
      setEmbaralhamentoAtual({});
      return;
    }

    if (perguntaAtual.type === 'closed') {
      const opcoesOriginais = Object.entries(perguntaAtual.options || {});
      const textos = opcoesOriginais.map(([, texto]) => texto);
      const textosProcessados = EMBARALHAR_OPCOES ? [...textos].sort(() => Math.random() - 0.5) : textos;

      const novaOrdem = {};
      Object.keys(perguntaAtual.options || {}).forEach((letra, index) => {
        novaOrdem[letra] = textosProcessados[index];
      });
      setEmbaralhamentoAtual(novaOrdem);

      setRespostas(prev => (prev[paginaAtual] !== undefined ? prev : { ...prev, [paginaAtual]: '' }));
    } else if (perguntaAtual.type === 'priority') {
      setRespostas(prev => {
        if (Array.isArray(prev[paginaAtual])) return prev;
        const initialOrder = Object.keys(perguntaAtual.options || {});
        return { ...prev, [paginaAtual]: initialOrder };
      });
      setEmbaralhamentoAtual({});
    } else if (perguntaAtual.type === 'scale') {
      setEmbaralhamentoAtual({});
      setRespostas(prev => {
        if (prev[paginaAtual] !== undefined) return prev;
        const min = (perguntaAtual.scale && typeof perguntaAtual.scale.min !== 'undefined')
          ? Number(perguntaAtual.scale.min)
          : 0;
        return { ...prev, [paginaAtual]: min };
      });
    } else if (perguntaAtual.type === 'open') {
      setEmbaralhamentoAtual({});
      setRespostas(prev => {
        if (prev[paginaAtual] !== undefined) return prev;
        if (perguntaAtual.hasDate) return { ...prev, [paginaAtual]: { text: '', date: '' } };
        return { ...prev, [paginaAtual]: '' };
      });
    } else {
      setEmbaralhamentoAtual({});
      setRespostas(prev => (prev[paginaAtual] !== undefined ? prev : { ...prev, [paginaAtual]: '' }));
    }
  }, [paginaAtual, teste]);

  const [dragState, setDragState] = useState({ fromIdx: null, overIdx: null });

  const onDragStartPriority = (fromIdx) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(fromIdx));
    setDragState({ fromIdx, overIdx: null });
  };

  const onDragOverPriority = (overIdx) => (e) => {
    e.preventDefault();
    setDragState(cur => ({ ...cur, overIdx }));
  };

  const onDropPriority = (toIdx) => (e) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    setRespostas(prev => {
      const atual = Array.isArray(prev[paginaAtual])
        ? prev[paginaAtual]
        : Object.keys((teste?.questions?.[paginaAtual]?.options) || {});
      if (from === toIdx || from < 0 || toIdx < 0 || from >= atual.length || toIdx >= atual.length) return prev;
      const nova = [...atual];
      const [m] = nova.splice(from, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtual]: nova };
    });
    setDragState({ fromIdx: null, overIdx: null });
  };

  const onDragEndPriority = () => setDragState({ fromIdx: null, overIdx: null });

  if (!turma || !teste) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.containerTop}>
          <h1 className={styles.title}>Carregando teste...</h1>
        </div>
      </div>
    );
  }

  const pergunta = !isIntroStep ? teste?.questions?.[paginaAtual] : null;
  const respostaAtual = !isIntroStep ? respostas[paginaAtual] : null;

  const priorityOrder = (!isIntroStep && pergunta?.type === 'priority')
    ? (Array.isArray(respostas[paginaAtual]) ? respostas[paginaAtual] : Object.keys(pergunta?.options || {}))
    : [];

  const perguntaRespondida = isIntroStep ? true : isPerguntaRespondida(pergunta, respostaAtual);

  let scaleConfig = null;
  if (!isIntroStep && pergunta?.type === 'scale') {
    const min = Number(pergunta?.scale?.min ?? 0);
    const max = Number(pergunta?.scale?.max ?? 10);
    const labels = (pergunta?.scale?.labels) || {};
    const rawValue = respostas[paginaAtual];
    const valueNum = Number(
      rawValue !== undefined && rawValue !== null && rawValue !== ''
        ? rawValue
        : min
    );

    const ticks = [];
    for (let v = min; v <= max; v++) {
      const lbl = labels[v] ?? String(v);
      ticks.push({ value: v, label: lbl });
    }

    const minLabel = labels[min] ?? String(min);
    const maxLabel = labels[max] ?? String(max);
    const currentLabel = labels[valueNum] ?? String(valueNum);

    scaleConfig = { min, max, ticks, minLabel, maxLabel, valueNum, currentLabel };
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.containerTop}>
        <h1 className={styles.title}>
          {empresa?.nome} - {turma?.nome}
        </h1>
      </div>

      <form className={styles.formulario}>
        <AnimatePresence mode="wait">
          <motion.div
            key={paginaAtual}
            className={styles.perguntaWrapper}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            {/* ✅ ETAPA INICIAL: descrição */}
            {isIntroStep ? (
              <>
                <p className={styles.pergunta}>{nomeTeste || 'Teste'}</p>

                <div className={styles.descricaoTesteMarkdown}>
                  {(descricaoTeste || '').trim() ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {descricaoTeste}
                    </ReactMarkdown>
                  ) : (
                    'Este teste não possui descrição cadastrada.'
                  )}
                </div>

                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#6b7c89',
                  }}
                >
                  Quando estiver pronto(a), clique em <strong>Começar</strong>.
                </div>
              </>
            ) : (
              <>
                <p className={styles.pergunta}>{pergunta?.question}</p>

                {pergunta?.type === 'closed' && (
                  <div className={styles.opcoesWrapper}>
                    {Object.keys(pergunta.options || {}).map((letra) => (
                      <div key={letra} className={styles.opcaoWrapper}>
                        <input
                          type="radio"
                          id={`q${paginaAtual}_${letra}`}
                          name={`resposta-${paginaAtual}`}
                          value={letra}
                          checked={respostas[paginaAtual] === letra}
                          onChange={() => handleChange(paginaAtual, letra)}
                          className={styles.radioInput}
                          required
                        />
                        <label htmlFor={`q${paginaAtual}_${letra}`} className={styles.opcaoLabel}>
                          {letra}) {embaralhamentoAtual[letra]}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {pergunta?.type === 'priority' && (
                  <div className={styles.priorityWrapper}>
                    <p className={styles.priorityHint}>
                      Arraste ou use as setas para ordenar (o topo é a{' '}
                      <strong>maior prioridade</strong>).
                    </p>

                    <ul className={styles.priorityList}>
                      {priorityOrder.map((letra, idx) => (
                        <li
                          key={letra}
                          className={`${styles.priorityItem} ${dragState.overIdx === idx ? styles.dragOver : ''}`}
                          draggable
                          onDragStart={onDragStartPriority(idx)}
                          onDragOver={onDragOverPriority(idx)}
                          onDrop={onDropPriority(idx)}
                          onDragEnd={onDragEndPriority}
                          title="Arraste para reordenar"
                        >
                          <span className={styles.dragHandle} aria-hidden="true">⋮⋮</span>
                          <span className={styles.priorityLetter}>{letra}</span>
                          <span className={styles.priorityText}>{(pergunta.options || {})[letra]}</span>

                          <div className={styles.priorityActions}>
                            <button
                              type="button"
                              className={`${styles.priorityBtn} ${styles.priorityBtnUp}`}
                              aria-label="Mover para cima"
                              onClick={handleClickUp(idx)}
                              disabled={idx === 0}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <FaArrowUp />
                            </button>

                            <button
                              type="button"
                              className={`${styles.priorityBtn} ${styles.priorityBtnDown}`}
                              aria-label="Mover para baixo"
                              onClick={handleClickDown(idx)}
                              disabled={idx === priorityOrder.length - 1}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <FaArrowDown />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {pergunta?.type === 'scale' && scaleConfig && (
                  <div className={styles.scaleWrapper}>
                    <div className={styles.scaleSliderHeader}>
                      <span className={styles.scaleMinLabel}>{scaleConfig.minLabel}</span>
                      <span className={styles.scaleCurrentValue}>
                        {scaleConfig.currentLabel}
                        {scaleConfig.currentLabel !== String(scaleConfig.valueNum) && <> ({scaleConfig.valueNum})</>}
                      </span>
                      <span className={styles.scaleMaxLabel}>{scaleConfig.maxLabel}</span>
                    </div>

                    <div className={styles.scaleSliderContainer}>
                      <input
                        type="range"
                        min={scaleConfig.min}
                        max={scaleConfig.max}
                        step={1}
                        value={scaleConfig.valueNum}
                        onChange={(e) => handleChange(paginaAtual, Number(e.target.value))}
                        className={`${styles.scaleSlider} ${isSmallScreen ? (styles.scaleSliderMobile || '') : ''}`}
                        style={isSmallScreen ? { height: 10 } : undefined}
                      />
                    </div>

                    <div className={styles.scaleTicks}>
                      {scaleConfig.ticks.map((tick) => (
                        <div key={tick.value} className={styles.scaleTick}>
                          <span className={styles.scaleTickLabel}>{tick.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pergunta?.type === 'open' && (
                  pergunta?.hasDate ? (
                    <div className={styles.openWithDateWrapper}>
                      <textarea
                        placeholder="Digite sua resposta"
                        value={
                          respostaAtual && typeof respostaAtual === 'object'
                            ? (respostaAtual.text || '')
                            : (respostaAtual || '')
                        }
                        onChange={(e) => {
                          const newText = e.target.value;
                          setRespostas(prev => {
                            const prevVal = prev[paginaAtual];
                            const prevObj = (prevVal && typeof prevVal === 'object')
                              ? prevVal
                              : { text: '', date: '' };
                            return { ...prev, [paginaAtual]: { ...prevObj, text: newText } };
                          });
                        }}
                        className={styles.textInput}
                        rows={isSmallScreen ? 6 : 5}
                        style={{
                          width: '100%',
                          minHeight: isSmallScreen ? 140 : 120,
                          resize: 'vertical',
                          overflow: 'auto',
                        }}
                        required
                      />

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                        <label
                          htmlFor={`q${paginaAtual}_date`}
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#002D4C',
                            marginTop: 2,
                          }}
                        >
                          Data referente à resposta
                        </label>

                        <input
                          id={`q${paginaAtual}_date`}
                          type="date"
                          value={respostaAtual && typeof respostaAtual === 'object' ? (respostaAtual.date || '') : ''}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setRespostas(prev => {
                              const prevVal = prev[paginaAtual];
                              const prevObj = (prevVal && typeof prevVal === 'object')
                                ? prevVal
                                : { text: '', date: '' };
                              return { ...prev, [paginaAtual]: { ...prevObj, date: newDate } };
                            });
                          }}
                          className={styles.dateInput}
                          required
                        />
                      </div>
                    </div>
                  ) : (
                    <textarea
                      placeholder="Digite sua resposta"
                      value={typeof respostaAtual === 'string' ? respostaAtual : ''}
                      onChange={(e) => handleChange(paginaAtual, e.target.value)}
                      className={styles.textInput}
                      rows={isSmallScreen ? 6 : 5}
                      style={{
                        width: '100%',
                        minHeight: isSmallScreen ? 140 : 120,
                        resize: 'vertical',
                        overflow: 'auto',
                      }}
                      required
                    />
                  )
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className={styles.navigationArea}>
          <button
            type="button"
            onClick={handleAnterior}
            disabled={paginaAtual === -1}
            className={styles.navBackButton}
          >
            Anterior
          </button>

          {isIntroStep ? (
            <button
              type="button"
              onClick={handleProximo}
              className={styles.navButton}
              disabled={totalPerguntas === 0}
            >
              Começar
            </button>
          ) : (
            paginaAtual < (teste?.questions?.length || 0) - 1 ? (
              <button
                type="button"
                onClick={handleProximo}
                className={styles.navButton}
                disabled={!perguntaRespondida}
              >
                Próxima
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className={styles.submitButton}
                disabled={!perguntaRespondida}
              >
                Enviar Respostas
              </button>
            )
          )}
        </div>

        <div className={styles.progressBarWrapper}>
          <div className={styles.progressBar} style={{ width: `${progresso}%` }} />
        </div>
      </form>

      <AnimatePresence>
        {mostrarModalFinalizacao && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className={styles.modalTitle}>🎉 Teste finalizado com sucesso!</h2>
              <p className={styles.modalText}>
                Parabéns por concluir o teste! Agora você pode acessar sua
                página ou enviar o resultado via WhatsApp.
              </p>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.modalButton}
                  onClick={() => navigate('/participante/page')}
                >
                  Ir para minha página
                </button>

                {/* ✅ AGORA FUNCIONA: chama o handler ajustado */}
                <button
                  type="button"
                  className={styles.modalButtonOutline}
                  onClick={handleEnviarWhatsApp}
                >
                  Enviar pelo WhatsApp
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ResponderTestePage;